import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  BookOpen,
  Plus,
  Edit,
  Trash2,
  Search,
  RefreshCw,
  Box,
  Building2,
  Globe,
  Calculator,
  Users,
  Inbox,
  Tag,
  ExternalLink,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { cn } from "../lib/utils";

const PRODUCT_ICON_MAP = { Building2, Globe, Calculator, Users, Inbox, Box };
const PRODUCT_COLOR_CLASSES = {
  orange: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  yellow: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  blue: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  violet: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  zinc: "bg-zinc-700/40 text-zinc-300 border-zinc-600/40",
};
const productClass = (color) => PRODUCT_COLOR_CLASSES[color] || PRODUCT_COLOR_CLASSES.zinc;

const emptyForm = {
  id: null,
  title: "",
  body: "",
  product_slug: "",     // single slug for the picker; serialized as [slug] or [] (= all)
  tags: "",
  is_active: true,
};

const SupportKbPage = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  const [filterProduct, setFilterProduct] = useState("all");
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      const params = { active_only: !includeInactive };
      if (filterProduct !== "all") params.product_slug = filterProduct;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/support-kb", { params });
      setArticles(data || []);
    } catch (e) {
      toast.error("Failed to load knowledge base");
    } finally {
      setLoading(false);
    }
  }, [filterProduct, search, includeInactive]);

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await api.get("/support-products", { params: { active_only: true } });
      setProducts(data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/support-kb/stats/by-product");
      setStats(data || {});
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { fetchArticles(); fetchStats(); }, [fetchArticles, fetchStats]);

  const productBySlug = (slug) => products.find((p) => p.slug === slug) || null;

  const openNew = () => {
    setForm({ ...emptyForm, product_slug: filterProduct !== "all" ? filterProduct : "" });
    setDialogOpen(true);
  };

  const openEdit = (article) => {
    setForm({
      id: article.id,
      title: article.title || "",
      body: article.body || "",
      product_slug: (article.product_slugs && article.product_slugs[0]) || "",
      tags: (article.tags || []).join(", "),
      is_active: article.is_active !== false,
    });
    setDialogOpen(true);
  };

  const saveArticle = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    try {
      setSaving(true);
      const tags = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
      const product_slugs = form.product_slug ? [form.product_slug] : [];
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        product_slugs,
        tags,
        is_active: form.is_active,
      };
      if (form.id) {
        await api.put(`/support-kb/${form.id}`, payload);
        toast.success("Article updated");
      } else {
        await api.post("/support-kb", payload);
        toast.success("Article created");
      }
      setDialogOpen(false);
      setForm(emptyForm);
      fetchArticles();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const deleteArticle = async (article) => {
    if (!window.confirm(`Delete "${article.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/support-kb/${article.id}`);
      toast.success("Deleted");
      fetchArticles();
      fetchStats();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const totalActive = articles.filter((a) => a.is_active !== false).length;

  return (
    <div className="space-y-5 p-6" data-testid="support-kb-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-emerald-400" /> Support Knowledge Base
          </h1>
          <p className="text-sm text-zinc-500">
            Curated answers the AI consults on every support chat. Save reliable answers here
            and the assistant will reuse them for the same product going forward.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="border-zinc-700"
            onClick={() => navigate("/settings/support-chats")}
          >
            <ExternalLink className="w-4 h-4 mr-1" /> Support Chats
          </Button>
          <Button variant="outline" size="sm" className="border-zinc-700" onClick={fetchArticles}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="kb-new">
            <Plus className="w-4 h-4 mr-1" /> New article
          </Button>
        </div>
      </div>

      {/* Stat strip — counts per product */}
      {products.length > 0 && (
        <Card className="bg-zinc-900/40 border-zinc-800">
          <CardContent className="p-3 flex flex-wrap gap-2 items-center">
            <button
              onClick={() => setFilterProduct("all")}
              className={cn(
                "px-2.5 py-1 rounded border text-xs flex items-center gap-1.5",
                filterProduct === "all" ? "bg-zinc-700 text-zinc-50 border-zinc-600" : "border-zinc-700 text-zinc-400 hover:bg-zinc-800/60"
              )}
            >
              <BookOpen className="w-3 h-3" /> All <span className="text-zinc-500">({totalActive})</span>
            </button>
            {products.map((p) => {
              const Icon = PRODUCT_ICON_MAP[p.icon] || Box;
              const count = (stats[p.slug] || 0) + (stats.__global__ || 0);
              const active = filterProduct === p.slug;
              return (
                <button
                  key={p.slug}
                  onClick={() => setFilterProduct(p.slug)}
                  className={cn(
                    "px-2.5 py-1 rounded border text-xs flex items-center gap-1.5",
                    active ? `${productClass(p.color)} ring-1 ring-emerald-400/40` : "border-zinc-700 text-zinc-400 hover:bg-zinc-800/60"
                  )}
                  title={p.description || p.name}
                >
                  <Icon className="w-3 h-3" />
                  {p.short_label || p.name}
                  <span className="text-zinc-500">({count})</span>
                </button>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Search + toggles */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <Input
            placeholder="Search title, body, tags..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-zinc-900 border-zinc-800"
          />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="accent-emerald-500"
          />
          Show inactive
        </label>
      </div>

      {/* Article list */}
      <div className="space-y-2">
        {loading ? (
          <p className="text-sm text-zinc-500 text-center py-8">Loading…</p>
        ) : articles.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">No articles yet. Save useful chat replies to KB or create one manually.</p>
          </div>
        ) : articles.map((a) => {
          const slugs = a.product_slugs || [];
          const isGlobal = slugs.length === 0;
          return (
            <Card
              key={a.id}
              className={cn(
                "bg-zinc-900/40 border-zinc-800 hover:border-zinc-700 transition-all cursor-pointer",
                !a.is_active && "opacity-50"
              )}
              onClick={() => openEdit(a)}
              data-testid={`kb-article-${a.id}`}
            >
              <CardContent className="p-3.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="text-sm font-medium text-zinc-100 truncate">{a.title}</h3>
                      {a.source === "from_chat" && (
                        <Badge variant="outline" className="text-[9px] border-purple-500/40 text-purple-300">from chat</Badge>
                      )}
                      {!a.is_active && (
                        <Badge variant="outline" className="text-[9px] border-zinc-600 text-zinc-500">inactive</Badge>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500 line-clamp-2 whitespace-pre-wrap">{a.body}</p>
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      {isGlobal ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] bg-zinc-700/40 text-zinc-300 border-zinc-600/40">
                          <BookOpen className="w-2.5 h-2.5" /> All products
                        </span>
                      ) : slugs.map((s) => {
                        const p = productBySlug(s);
                        if (!p) return <span key={s} className="text-[9px] text-zinc-500">{s}</span>;
                        const Icon = PRODUCT_ICON_MAP[p.icon] || Box;
                        return (
                          <span key={s} className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px]", productClass(p.color))}>
                            <Icon className="w-2.5 h-2.5" /> {p.short_label || p.name}
                          </span>
                        );
                      })}
                      {(a.tags || []).map((t) => (
                        <span key={t} className="inline-flex items-center gap-0.5 text-[9px] text-zinc-500">
                          <Tag className="w-2.5 h-2.5" /> {t}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-zinc-400 hover:text-zinc-100"
                      onClick={(e) => { e.stopPropagation(); openEdit(a); }}
                      title="Edit"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-zinc-400 hover:text-red-400"
                      onClick={(e) => { e.stopPropagation(); deleteArticle(a); }}
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="text-[10px] text-zinc-600 mt-2">
                  {a.created_by_name || a.created_by} · updated {new Date(a.updated_at).toLocaleDateString()}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit / new dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-emerald-400" />
              {form.id ? "Edit article" : "New article"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-zinc-500 uppercase">Title</label>
              <Input
                value={form.title}
                onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
                placeholder="Short, search-friendly title"
                className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1"
                data-testid="kb-form-title"
              />
            </div>
            <div>
              <label className="text-[11px] text-zinc-500 uppercase">Body (markdown ok)</label>
              <Textarea
                value={form.body}
                onChange={(e) => setForm((s) => ({ ...s, body: e.target.value }))}
                rows={10}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1 text-sm font-mono"
                data-testid="kb-form-body"
              />
              <p className="text-[10px] text-zinc-500 mt-1">
                The AI sees this verbatim when answering similar questions. Be specific.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-zinc-500 uppercase">Product</label>
                <Select
                  value={form.product_slug || "__all__"}
                  onValueChange={(v) => setForm((s) => ({ ...s, product_slug: v === "__all__" ? "" : v }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="__all__">All products</SelectItem>
                    {products.map((p) => (
                      <SelectItem key={p.slug} value={p.slug}>{p.short_label || p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] text-zinc-500 uppercase">Tags (comma separated)</label>
                <Input
                  value={form.tags}
                  onChange={(e) => setForm((s) => ({ ...s, tags: e.target.value }))}
                  placeholder="login, payments, ..."
                  className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1"
                />
              </div>
            </div>
            <label className="flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((s) => ({ ...s, is_active: e.target.checked }))}
                className="accent-emerald-500"
              />
              Active (the AI will see this)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-zinc-700" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={saveArticle}
              disabled={saving || !form.title.trim() || !form.body.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="kb-form-save"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportKbPage;
